// src/Lexprompt/pages/NewPromptPage.jsx

import React, { useState } from "react";
import { db, storage } from "../firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import "./NewPromptPage.css";

// ─── All possible metadata fields ─────────────────────────────────────────
const FIELD_TYPES = [
  { key: "title", label: "Title", type: "text", required: true },
  { key: "body", label: "Prompt", type: "textarea", required: true },
  {
    key: "department",
    label: "Department",
    type: "select",
    options: ["Engineering", "Sales", "Marketing", "HR", "Finance", "Other"],
    required: false,
  },
  { key: "team", label: "Team", type: "text", required: false },
  { key: "company", label: "Company", type: "text", required: false },
  { key: "imageUrl", label: "Image URL", type: "text", required: false },
  { key: "videoUrl", label: "Video URL", type: "text", required: false },
  {
    key: "attachment",
    label: "Attachment (file)",
    type: "file",
    required: false,
  },
  {
    key: "nextPrompt",
    label: "Next Workflow Prompt",
    type: "text",
    required: false,
  },
  {
    key: "prevPrompt",
    label: "Previous Workflow Prompt",
    type: "text",
    required: false,
  },
];

export default function NewPromptPage({ onClose }) {
  // ─── By default, show Title, Prompt, and Attachment ───
  const defaultKeys = ["title", "body", "attachment"];
  const initialDropped = FIELD_TYPES.filter((f) =>
    defaultKeys.includes(f.key)
  );

  // ─── droppedFields state initializes with Title, Body, Attachment ────
  const [droppedFields, setDroppedFields] = useState(initialDropped);

  // ─── Initialize values only for the default fields ─────────────────────
  const initialValues = FIELD_TYPES.reduce((acc, field) => {
    if (defaultKeys.includes(field.key)) {
      acc[field.key] = field.type === "file" ? null : "";
    }
    return acc;
  }, {});
  const [values, setValues] = useState(initialValues);

  const [submitError, setSubmitError] = useState("");

  //
  // ─── Drag‐and‐Drop Handlers ───────────────────────────────────────────────
  //
  const handleDragStart = (e, fieldKey) => {
    e.dataTransfer.setData("text/plain", fieldKey);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const fieldKey = e.dataTransfer.getData("text/plain");
    if (!fieldKey) return;

    // If already in canvas, do nothing
    if (droppedFields.some((f) => f.key === fieldKey)) return;

    const fieldConfig = FIELD_TYPES.find((f) => f.key === fieldKey);
    if (!fieldConfig) return;

    setDroppedFields((prev) => [...prev, fieldConfig]);
    setValues((prev) => ({
      ...prev,
      [fieldKey]: fieldConfig.type === "file" ? null : "",
    }));
  };

  const removeField = (fieldKey) => {
    setDroppedFields((prev) => prev.filter((f) => f.key !== fieldKey));
    setValues((prev) => {
      const newVals = { ...prev };
      delete newVals[fieldKey];
      return newVals;
    });
  };

  //
  // ─── Handle text/textarea/select/file inputs ─────────────────────────────
  //
  const handleChange = (e, fieldKey, type) => {
    if (type === "file") {
      setValues((prev) => ({
        ...prev,
        [fieldKey]: e.target.files[0] || null,
      }));
    } else {
      setValues((prev) => ({ ...prev, [fieldKey]: e.target.value }));
    }
  };

  //
  // ─── Submit handler: upload file (if any) to Storage, save URL + other metadata to Firestore ───────────────────
  //
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    // Validate required fields present in the canvas
    for (let fieldConfig of droppedFields) {
      if (fieldConfig.required && !values[fieldConfig.key]) {
        setSubmitError(`"${fieldConfig.label}" is required.`);
        return;
      }
    }

    // 1) Build a Firestore payload
    let payload = {
      createdAt: serverTimestamp(),
    };

    // 2) If there's a file to upload, do that first
    if (values.attachment instanceof File) {
      try {
        const file = values.attachment;
        const timestamp = Date.now();
        // Storage reference path: “attachments/{timestamp}-{filename}”
        const fileRef = storageRef(
          storage,
          `attachments/${timestamp}-${file.name}`
        );
        // Upload the file bytes
        await uploadBytes(fileRef, file);
        // Retrieve a public download URL
        const downloadURL = await getDownloadURL(fileRef);
        // Store that URL under “attachment” in Firestore
        payload.attachment = downloadURL;
      } catch (err) {
        console.error("Error uploading attachment:", err);
        setSubmitError("Failed to upload attachment. Please try again.");
        return;
      }
    }

    // 3) For all other dropped fields (text, textarea, select), include them in payload
    for (let fieldConfig of droppedFields) {
      const { key, type } = fieldConfig;
      if (type === "file") {
        // Already handled above
        continue;
      } else {
        payload[key] = values[key] || "";
      }
    }

    // 4) Write the new document to Firestore
    try {
      const colRef = collection(db, "prompts");
      await addDoc(colRef, payload);
      onClose();
    } catch (err) {
      console.error("Error creating prompt:", err);
      setSubmitError("Failed to save prompt. Please try again.");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => {
          // Prevent clicks inside the modal from closing it
          e.stopPropagation();
        }}
      >
        {/* ─── Modal Header ─────────────────────────────────────────── */}
        <div className="modal-header">
          <h2>Create New Prompt</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {/* ─── Modal Body (Toolbox + Canvas) ───────────────────────── */}
        <div className="modal-body">
          {/* ─── Toolbox: list all FIELD_TYPES ────────────────────── */}
          <div className="field-toolbox">
            <h3>Drag Fields Into Form</h3>
            <ul>
              {FIELD_TYPES.map((field) => (
                <li key={field.key}>
                  <div
                    className={`toolbox-item ${
                      droppedFields.some((f) => f.key === field.key)
                        ? "disabled"
                        : ""
                    }`}
                    draggable={
                      !droppedFields.some((f) => f.key === field.key)
                    }
                    onDragStart={(e) => handleDragStart(e, field.key)}
                  >
                    {field.label}
                    {field.required && <span className="required">*</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* ─── Form Canvas ───────────────────────────────────────── */}
          <div
            className="form-canvas"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <h3>Form Preview</h3>

            {droppedFields.length === 0 && (
              <p className="canvas-empty">
                Drag fields from the left into this area to build your prompt.
              </p>
            )}

            <form onSubmit={handleSubmit}>
              {droppedFields.map((fieldConfig) => {
                const { key, label, type, options, required } = fieldConfig;
                const value = values[key] || "";

                return (
                  <div className="form-field" key={key}>
                    <label>
                      {label}
                      {required && <span className="required">*</span>}
                    </label>

                    <div className="field-input-wrapper">
                      {type === "text" && (
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => handleChange(e, key, type)}
                          required={required}
                        />
                      )}

                      {type === "textarea" && (
                        <textarea
                          value={value}
                          onChange={(e) => handleChange(e, key, type)}
                          required={required}
                        />
                      )}

                      {type === "select" && (
                        <select
                          value={value}
                          onChange={(e) => handleChange(e, key, type)}
                          required={required}
                        >
                          <option value="">— Choose —</option>
                          {options.map((opt) => (
                            <option value={opt} key={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      )}

                      {type === "file" && (
                        <input
                          type="file"
                          onChange={(e) => handleChange(e, key, type)}
                          required={required}
                        />
                      )}
                    </div>

                    <button
                      type="button"
                      className="remove-field-btn"
                      onClick={() => removeField(key)}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}

              {submitError && <p className="np-error-text">{submitError}</p>}

              {/* ─── Modal Actions ─────────────────────────────────── */}
              <div className="modal-actions">
                <button type="submit" className="btn-save">
                  Save Prompt
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
